/**
 * Chart Section Block
 *
 * A note section whose body is the patient chart rather than prose. The
 * backend types the section (`kind`) and the note view mounts this instead of
 * a BlockNote editor — there is no custom BlockNote block, no editor to
 * escape from, and no `edited_text` PATCH while the provider works: the cards
 * write through the patient chart CRUD endpoints, whose invalidations already
 * reach every other note surface reading the same tags (the patient context
 * indicators above the note included).
 *
 * It renders in *every* view mode. While the note is interactive and a
 * patient is in scope, the body is the live chart card. Otherwise — a
 * read-only view mode, a signed note, generation still streaming, or a note
 * with no patient — the body is the section's resolved text, which after the
 * author's attestation is the frozen table the backend captured. Choosing
 * between those two is the only thing `editable` decides; whether this
 * component renders at all is decided by `kind`, one level up.
 */
import React from 'react';
import { EditorView } from '@components/BlockNote/EditorView';
import { V1AllergiesCard } from '@features/medications-workspace/containers/V1AllergiesCard';
import { V1DiagnosesCard } from '@features/medications-workspace/containers/V1DiagnosesCard';
import { V1MedicationsCard } from '@features/medications-workspace/containers/V1MedicationsCard';
import { NoteSectionHeader } from '@features/notes/components/NoteSectionPresentation';
import { useSubjectLanguageVariants } from '@features/user';
import { Chip } from '@jotpsych/ui';
import { Box, Typography } from '@mui/material';

import type { SectionKindComponentProps } from './sectionKindRegistry';

/** Analytics surface the embedded cards stamp on their own interaction events. */
const CHART_BLOCK_SURFACE = 'note_chart_block' as const;

const CHART_CARDS: Partial<Record<string, React.FC<{ patientId: string }>>> = {
  active_diagnoses: ({ patientId }) => (
    <V1DiagnosesCard
      patientId={patientId}
      variant="embedded"
      hideTitle
      surface={CHART_BLOCK_SURFACE}
    />
  ),
  medications: ({ patientId }) => (
    <V1MedicationsCard
      patientId={patientId}
      variant="embedded"
      hideTitle
      surface={CHART_BLOCK_SURFACE}
    />
  ),
  allergies: ({ patientId }) => (
    <V1AllergiesCard
      patientId={patientId}
      variant="embedded"
      hideTitle
      surface={CHART_BLOCK_SURFACE}
    />
  ),
};

export const ChartSectionBlock: React.FC<SectionKindComponentProps> = ({
  kind,
  name,
  editable,
  patientId,
  markdown,
}) => {
  const { singular } = useSubjectLanguageVariants();
  const Card = CHART_CARDS[kind];
  const live = Card !== undefined && editable && patientId !== undefined;

  return (
    <Box data-chart-section-kind={kind} data-chart-section-live={live ? 'true' : 'false'}>
      <NoteSectionHeader
        name={name}
        headerAfterTitle={
          live ? (
            <Chip
              variant="outlined"
              color="warning"
              size="small"
              label="Live from chart"
              sx={{ flexShrink: 0 }}
            />
          ) : undefined
        }
      />

      <Box sx={{ pt: 1.5 }}>
        {live && Card !== undefined && patientId !== undefined ? (
          <Card patientId={patientId} />
        ) : (
          <EditorView markdown={markdown} editable={false} />
        )}
      </Box>

      {live && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {`Edits here update the ${singular} chart. This table freezes into the note at signature.`}
        </Typography>
      )}
    </Box>
  );
};

ChartSectionBlock.displayName = 'ChartSectionBlock';
